import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    defer,
    map,
    Observable,
    tap,
    throwError,
} from 'rxjs';
import type {
    IPacsInstanceManifest,
    IPacsJob,
    IPacsPrepareResult,
    IPacsSeriesManifest,
    IPacsStudyPage,
    IPacsViewerSelectedState,
} from '../interfaces/pacs-viewer.interfaces';
import type {
    TPacsViewerResolution,
    TPacsViewerSessionState,
} from '../interfaces/pacs-viewer.types';
import { ConfigService } from './application/config.service.';
import { HttpNewService } from './application/http-new.service';

const PACS_VIEWER_SESSIONS_URL = '/pacs/viewer-sessions';
const HISTORY_EVENTS_URL = '/history/events';

@Injectable({
    providedIn: 'root',
})
export class PacsViewerService {
    private readonly sessionStateSubject =
        new BehaviorSubject<TPacsViewerSessionState>({
            status: 'IDLE',
            historyType: null,
            historyId: null,
        });

    public readonly sessionState$ = this.sessionStateSubject.asObservable();

    public get sessionState(): TPacsViewerSessionState {
        return this.sessionStateSubject.value;
    }

    public constructor(
        private readonly http: HttpNewService,
        private readonly config: ConfigService,
    ) {}

    public setHistoryContext(
        historyType: string | null,
        historyId: number | null,
    ): void {
        this.sessionStateSubject.next({
            status: 'IDLE',
            historyType,
            historyId,
        });
    }

    public resolveSession(
        historyType: string,
        historyId: number,
    ): Observable<TPacsViewerResolution> {
        return defer(() => {
            this.sessionStateSubject.next({
                status: 'RESOLVING',
                historyType,
                historyId,
            });

            return this.http.post<TPacsViewerResolution>(
                `${HISTORY_EVENTS_URL}/${this.encode(historyType)}/${historyId}/pacs-viewer-sessions`,
                null,
            );
        }).pipe(
            tap((resolution) => this.sessionStateSubject.next(resolution)),
            catchError((error: unknown) => {
                this.sessionStateSubject.next({ status: 'ERROR', error });
                return throwError(() => error);
            }),
        );
    }

    public getStudies(
        lookupSessionRef: string,
        pageRef?: string,
    ): Observable<IPacsStudyPage> {
        const url = `${this.getSessionUrl(lookupSessionRef)}/studies`;

        return this.http
            .get<IPacsStudyPage>(
                url,
                pageRef !== undefined ? { pageRef } : undefined,
            )
            .pipe(
                tap((page) =>
                    this.updateAmbiguousStudies(
                        lookupSessionRef,
                        page,
                        pageRef !== undefined,
                    ),
                ),
            );
    }

    public selectStudy(
        lookupSessionRef: string,
        studyRef: string,
    ): Observable<IPacsViewerSelectedState> {
        const url = `${this.getSessionUrl(lookupSessionRef)}/studies/${this.encode(studyRef)}/select`;

        return this.http
            .post<Omit<IPacsViewerSelectedState, 'status'>>(url, null)
            .pipe(
                map((selectedStudy) => ({
                    status: 'SELECTED' as const,
                    ...selectedStudy,
                })),
                tap((selectedStudy) =>
                    this.sessionStateSubject.next(selectedStudy),
                ),
            );
    }

    public getSeries(
        selectedSessionRef: string,
    ): Observable<IPacsSeriesManifest> {
        return this.http.get<IPacsSeriesManifest>(
            `${this.getSessionUrl(selectedSessionRef)}/series`,
        );
    }

    public prepareSeries(
        selectedSessionRef: string,
        seriesRef: string,
    ): Observable<IPacsPrepareResult> {
        return this.http.post<IPacsPrepareResult>(
            `${this.getSessionUrl(selectedSessionRef)}/series/${this.encode(seriesRef)}/prepare`,
            null,
        );
    }

    public getJob(
        selectedSessionRef: string,
        jobRef: string,
    ): Observable<IPacsJob> {
        return this.http.get<IPacsJob>(
            `${this.getSessionUrl(selectedSessionRef)}/jobs/${this.encode(jobRef)}`,
        );
    }

    public getInstances(
        selectedSessionRef: string,
        seriesRef: string,
    ): Observable<IPacsInstanceManifest> {
        return this.http.get<IPacsInstanceManifest>(
            `${this.getSessionUrl(selectedSessionRef)}/series/${this.encode(seriesRef)}/instances`,
        );
    }

    public getDicomInstanceUrl(
        selectedSessionRef: string,
        instanceRef: string,
    ): string {
        return this.getBackendUrl(
            `${this.getSessionUrl(selectedSessionRef)}/instances/${this.encode(instanceRef)}/dicom`,
        );
    }

    public prepareArchive(
        selectedSessionRef: string,
    ): Observable<IPacsPrepareResult> {
        return this.http.post<IPacsPrepareResult>(
            `${this.getSessionUrl(selectedSessionRef)}/archive/prepare`,
            null,
        );
    }

    public getArchiveUrl(
        selectedSessionRef: string,
        archiveRef: string,
    ): string {
        return this.getBackendUrl(
            `${this.getSessionUrl(selectedSessionRef)}/archives/${this.encode(archiveRef)}`,
        );
    }

    public resetSession(): void {
        this.setHistoryContext(null, null);
    }

    private getSessionUrl(sessionRef: string): string {
        return `${PACS_VIEWER_SESSIONS_URL}/${this.encode(sessionRef)}`;
    }

    private getBackendUrl(path: string): string {
        return `${this.config.getValue('hostBackend')}${path}`;
    }

    private updateAmbiguousStudies(
        lookupSessionRef: string,
        page: IPacsStudyPage,
        append: boolean,
    ): void {
        const currentState = this.sessionState;
        if (
            currentState.status !== 'AMBIGUOUS' ||
            currentState.lookupSessionRef !== lookupSessionRef
        )
            return;

        const studies = append
            ? [...currentState.studies, ...page.studies].filter(
                  (study, index, allStudies) =>
                      allStudies.findIndex(
                          (candidate) => candidate.studyRef === study.studyRef,
                      ) === index,
              )
            : page.studies;

        this.sessionStateSubject.next({
            ...currentState,
            studies,
            nextPageRef: page.nextPageRef,
        });
    }

    private encode(value: string): string {
        return encodeURIComponent(value);
    }
}
